<template>
  <v-container fluid class="positions-readiness-table">
    <div class="px-5 mb-5">
      <SearchBar
        @onSearch="handleSearch"
        type="button"
        :label="appliedFilters.length > 0 ? 'More Filters' : 'Filters'"
        :length="appliedFilters.length"
        :chipText="filterLabels"
        ghostText="Search Position"
        :add="false"
        @onClick="showFilterDialog = true"
        @removeFilter="handleRemoveFilter"
        :valueSearch="searchText"
      />
    </div>

    <DataTable
      v-if="tableRows.length !== 0"
      :pagination="paginationModel"
      :headers="headers"
      :data="tableRows"
      :noHoverEffect="true"
      @onRequest="requestData"
    >
      <template #no-data />

      <template #exclude="{ row }">
        <div class="exclude-cell">
          <SwitchButton
            :label="`${row.position.data.id}`"
            :showTitle="false"
            :containerWidth="'56px'"
            :value="row.exclude.data.value"
            :toggleLoader="toggleLoader"
            @onChange="onExcludeChange"
          />
        </div>
      </template>

      <template #position="{ row }">
        <div class="position-cell">
          <ExOrgObjectCodeName
            :code="getPositionCode(row.position.data)"
            :name="row.position.data.name"
            size="medium"
            clickPopup
            truncate
            @openPopup="handlePositionClick(row.position.data)"
          />
        </div>
      </template>

      <template #occupied_by="{ row }">
        <div class="occupied-by-cell">
          <ReadinessOrgOccupationTag
            :occupied_by="getOccupant(row.position.data)"
            :firstName="getOccupantFirstName(row.position.data)"
            :lastName="getOccupantLastName(row.position.data)"
            :position="row.position.data.name"
            :photoUrl="getOccupantPhoto(row.position.data)"
            :availability="1"
            :showAvailability="false"
            :isAllocated="!!getOccupant(row.position.data)"
            :showLinkUnderline="false"
            :hideAvailabilitySelector="true"
            :isHoverEnabled="false"
            :showImportanceSelector="false"
          />
        </div>
      </template>

      <template #readiness="{ row }">
        <div class="readiness-cell">
          <EmployeeReadinessBar
            :status="getBarStatus(row.position.data)"
            :recalc="false"
            :open="getBarOpen(row.position.data)"
            :code="getPositionCode(row.position.data)"
            :name="row.position.data.name"
            :readiness="getBarValue(row.position.data)"
          />
        </div>
      </template>

      <template #actions="{ row }">
        <div class="actions-cell">
          <ChatterIcon @onClick="$emit('onChatterClick', row.position.data)" />
        </div>
      </template>
    </DataTable>

    <NoRecordFound
      v-else
      title="Positions Readiness"
      message="Positions Readiness"
      :subtitle="''"
      class="no-record-found"
      :shouldShowAddAction="false"
      :showClearAction="true"
      :clearLabel="clearButtonLabel"
      :showActiveCounter="false"
      @onClear="clearFilters"
    />

    <FilterPositionsReadinessModal
      :value="showFilterDialog"
      @input="showFilterDialog = false"
      @close="showFilterDialog = false"
      @apply="handleFilterChange"
      @reset="handleResetFilters"
      @search="handleDepartmentSearch"
      @clear="handleDepartmentClear"
      :initialVacancy="initialVacancy"
      :initialExcluded="initialExcluded"
      :initialDepartment="initialDepartment"
      :initialScope="initialScope"
      :departments="departments"
      :loading="departmentLoading"
    />
  </v-container>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import DataTable from "@components/DataTable/DataTable.vue";
import SearchBar from "@components/SearchBar/SearchBar.vue";
import ExOrgObjectCodeName from "@components/OrgObjectCodeName/ExOrgObjectCodeName.vue";
import ReadinessOrgOccupationTag from "@components/ReadinessOrgOccupationTag/ReadinessOrgOccupationTag.vue";
import SwitchButton from "@components/SwitchButton/SwitchButton.vue";
import EmployeeReadinessBar from "@components/EmployeeReadinessBar/EmployeeReadinessBar.vue";
import ChatterIcon from "@components/ChatterIcon/ChatterIcon.vue";
import NoRecordFound from "@components/NoRecordFound/NoRecordFound.vue";
import FilterPositionsReadinessModal from "@components/FilterPositionsReadinessModal/FilterPositionsReadinessModal.vue";

const props = defineProps({
  positions: {
    type: Array,
    default: () => [],
  },
  filterGroups: {
    type: Array,
    default: () => [],
  },
  appliedFilters: {
    type: Array,
    default: () => [],
  },
  searchText: {
    type: String,
    default: "",
  },
  totalCount: {
    type: Number,
    default: 0,
  },
  currentPage: {
    type: Number,
    default: 1,
  },
  itemsPerPage: {
    type: Number,
    default: 10,
  },
  toggleLoader: {
    type: Boolean,
    default: false,
  },
  departments: {
    type: Array,
    default: () => [],
  },
  departmentLoading: {
    type: Boolean,
    default: false,
  },
  initialDepartment: {
    type: Object,
    default: null,
  },
  initialScope: {
    type: String,
    default: "subtree",
  },
});

const emit = defineEmits([
  "onChatterClick",
  "onSearch",
  "onFilterChange",
  "onRemoveFilter",
  "onClearFilters",
  "onExcludeChange",
  "onPaginationChange",
  "onPositionClick",
]);

const showFilterDialog = ref(false);
const clearSelection = ref("");
const excludeById = ref({});
const paginationModel = ref({
  page: 1,
  total: 0,
  perPage: 10,
});

const filterLabels = computed(() => {
  const valueToLabelMap = {};
  (props.filterGroups || []).forEach((group) => {
    (group.items || []).forEach((item) => {
      valueToLabelMap[item.value] = item.label;
    });
  });
  return (props.appliedFilters || []).map((value) => {
    if (value && typeof value === "object") {
      return value.name || value.label || "";
    }
    return valueToLabelMap[value] || value;
  });
});

const initialVacancy = computed(() => {
  const values = props.appliedFilters || [];
  if (values.includes("vacant")) return "vacant";
  if (values.includes("not_vacant")) return "not_vacant";
  return null;
});

const initialExcluded = computed(() => {
  const values = props.appliedFilters || [];
  if (values.includes("excluded")) return "excluded";
  if (values.includes("not_excluded")) return "not_excluded";
  return null;
});

const headers = computed(() => [
  { text: "Exclude", sortable: false, value: "exclude", width: "120px" },
  { text: "Position", sortable: true, value: "position", width: "300px" },
  {
    text: "Occupied by",
    sortable: false,
    value: "occupied_by",
    width: "260px",
  },
  {
    text: "Position Readiness",
    sortable: false,
    value: "readiness",
    width: "240px",
    centerField: true,
  },
  { text: "", sortable: false, value: "actions", width: "80px" },
]);

const tableRows = computed(() =>
  (props.positions || []).map((position) => {
    const id = position?.id;
    // exclude_from_avg is the source of truth; fallback to local optimistic state
    const excludeValue =
      position.exclude_from_avg !== undefined
        ? position.exclude_from_avg
        : excludeById.value?.[id] ?? false;
    return {
      exclude: { slot: "exclude", data: { id, value: excludeValue } },
      position: { slot: "position", data: position },
      occupied_by: { slot: "occupied_by", data: position },
      readiness: { slot: "readiness", data: position },
      actions: { slot: "actions", data: position },
    };
  })
);

const clearButtonLabel = computed(() => {
  const hasSearch = props.searchText && props.searchText.trim().length > 0;
  const hasFilters = props.appliedFilters && props.appliedFilters.length > 0;
  if (hasSearch && hasFilters) {
    return "Clear All";
  } else if (hasSearch) {
    return "Clear Search";
  } else if (hasFilters) {
    return "Clear Filter";
  } else {
    return "Clear Filters";
  }
});

function getOccupant(position) {
  return position?.occupant || null;
}
function getOccupantFirstName(position) {
  const fullName = position?.occupant?.name || "";
  return fullName.split(" ")[0] || "";
}
function getOccupantLastName(position) {
  const fullName = position?.occupant?.name || "";
  return fullName.split(" ").slice(1).join(" ") || "";
}
function getOccupantPhoto(position) {
  return position?.occupant?.avatar || "";
}
function getPositionCode(position) {
  const raw = position?.code_str || position?.code || "";
  return raw.split(" ")[0] || "";
}
function getBarValue(position) {
  if (!position) return null;
  const occupant = getOccupant(position);
  if (!occupant) return 0;
  const v = position.position_readiness;
  return v == null ? 0 : Number(v);
}
function getBarStatus() {
  return "Has Assignments";
}
function getBarOpen(position) {
  return !!getOccupant(position);
}
function clearFilters() {
  emit("onClearFilters");
  paginationModel.value.page = 1;
}
function handleSearch(text) {
  emit("onSearch", text);
  paginationModel.value.page = 1;
}
function requestData(data) {
  if (data && data.page) {
    paginationModel.value.page = data.page;
    emit("onPaginationChange", {
      page: data.page,
      itemsPerPage: paginationModel.value.perPage,
    });
  }
}
function handleRemoveFilter(removedFilter) {
  clearSelection.value = removedFilter;
  emit("onRemoveFilter", removedFilter);
}
function handleFilterChange(filters) {
  showFilterDialog.value = false;
  emit("onFilterChange", filters);
}
function handleResetFilters() {
  emit("onFilterChange", {
    vacancy: null,
    excluded: null,
    department: null,
    scope: null,
  });
}
function handleDepartmentSearch(query) {
  emit("onDepartmentSearch", query);
}
function handleDepartmentClear() {
  emit("onDepartmentClear");
}
function onExcludeChange({ label, value }) {
  const id = Number(label);
  if (!Number.isNaN(id)) {
    excludeById.value = { ...excludeById.value, [id]: !!value };
    const position = props.positions.find((pos) => pos.id === id);
    if (position) {
      emit("onExcludeChange", { position, value: { value: !!value } });
    }
  }
}
function handlePositionClick(position) {
  emit("onPositionClick", position);
}

watch(
  () => props.totalCount,
  (newVal) => {
    paginationModel.value.total = newVal || 0;
  },
  { immediate: true }
);

watch(
  () => props.currentPage,
  (newVal) => {
    paginationModel.value.page = newVal || 1;
  },
  { immediate: true }
);

watch(
  () => props.itemsPerPage,
  (newVal) => {
    paginationModel.value.perPage = newVal || 10;
  },
  { immediate: true }
);

watch(
  () => props.positions,
  (list) => {
    (list || []).forEach((position) => {
      if (!position || typeof position.id === "undefined") return;
      if (position.exclude_from_avg !== undefined) {
        excludeById.value = {
          ...excludeById.value,
          [position.id]: position.exclude_from_avg,
        };
      }
    });
  },
  { immediate: true, deep: true }
);

defineExpose({
  filterLabels,
  initialVacancy,
  initialExcluded,
  tableRows,
  clearButtonLabel,
  paginationModel,
  getOccupant,
  getOccupantFirstName,
  getOccupantLastName,
  getOccupantPhoto,
  getPositionCode,
  getBarValue,
  getBarStatus,
  getBarOpen,
  clearFilters,
  handleSearch,
  requestData,
  handleRemoveFilter,
  handleFilterChange,
  handleResetFilters,
  handleDepartmentSearch,
  handleDepartmentClear,
  onExcludeChange,
  handlePositionClick,
});
</script>

<style scoped>
.positions-readiness-table {
  border: 1px solid #d0d5dd;
  box-shadow: 0px 1px 2px 0px #1018280d;
  background: #ffffff;
  border-radius: 10px;
  padding: 24px;
}
.exclude-cell {
  display: flex;
  align-items: center;
}
.position-cell {
  margin: 12px 0;
  width: 100%;
}
.occupied-by-cell {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin: 12px 0;
}
.readiness-cell {
  display: flex;
  align-items: center;
  justify-content: center;
}
.actions-cell {
  display: flex;
  align-items: center;
  justify-content: center;
}
/* Normalize EmployeeReadinessBar spacing inside DataTable cells */
::v-deep .employee-readiness-bar {
  margin: 0 !important; /* override EmployeeReadinessBar's own spacing within table cells */
  left: 0 !important; /* cancel EmployeeReadinessBar's default left offset inside cells */
}
/* SwitchButton has a default left offset; neutralize it in table layouts */
::v-deep .editing-switch-container {
  left: 0 !important; /* cancel SwitchButton's default left offset inside the table */
}
</style>
